import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — la matriz de competencias, `/api/competencies`. Módulo 11, opción 4.
 *
 * Contrato del vanilla, llamada a llamada, con dos diferencias escritas:
 *
 * - Los cursos que se pueden vincular salen de `useCursos` del catálogo, con su
 *   tope de 200, y no de `/courses/?limit=100`, igual que en las rutas.
 * - Al escribir un requisito por cargo se ofrecen los del catálogo de cargos
 *   (`/admin/catalogs/positions`). El cargo se escribe a mano en dos sitios —el
 *   perfil y el requisito— y cualquier diferencia dejaba a la persona fuera.
 *   Desde el 2026-09-22 el servidor ya compara sin mayúsculas ni espacios; las
 *   sugerencias quitan el resto de las erratas.
 *
 * Y una que es del servidor: crear una competencia con el nombre de una borrada
 * la RESTAURA —`name` es único en la tabla— y la respuesta lo dice en
 * `restaurada`.
 */

export interface CompetenciaResumen {
  id: number;
  name: string;
  description?: string | null;
  area_id?: number | null;
  area_name?: string | null;
  level?: string | null;
  level_label?: string | null;
  course_count: number;
  requirement_count: number;
}

export interface CursoVinculado {
  course_id: number;
  title: string;
}

/** Un requisito es de un cargo o de un área. El servidor admite los dos a la
 *  vez, pero su búsqueda los trata como «o» y la pantalla solo enseñaba uno:
 *  aquí se elige uno de los dos. */
export interface Requisito {
  id: number;
  target_position?: string | null;
  area_id?: number | null;
  area_name?: string | null;
}

export interface CompetenciaDetalle extends CompetenciaResumen {
  courses: CursoVinculado[];
  requirements: Requisito[];
}

export interface CursoRecomendado {
  course_id: number;
  title: string;
  cover_icon?: string | null;
  cover_icon_color?: string | null;
}

export interface CompetenciaPropia {
  id: number;
  name: string;
  level?: string | null;
  level_label?: string | null;
}

export interface MisCompetencias {
  position?: string | null;
  acquired: CompetenciaPropia[];
  gaps: Array<CompetenciaPropia & { recommended_courses: CursoRecomendado[] }>;
  /** Cuántas espera su cargo o su área. Cero no es «lo cubres todo»: es que no
   *  hay ninguna definida. */
  expected_total: number;
  acquired_of_expected: number;
}

export interface CoberturaDeCompetencia {
  id: number;
  name: string;
  covered: number;
  total: number;
  pct: number;
}

export interface CargoDeLaMatriz {
  position: string;
  user_count: number;
  competencies: CoberturaDeCompetencia[];
}

export interface CuerpoDeCompetencia {
  name: string;
  description: string | null;
  area_id: number | null;
  level: string | null;
}

export const clavesDeCompetencias = {
  /** El prefijo de todo: cualquier cambio mueve la lista, la matriz y lo propio. */
  todas: ['formacion', 'competencias'] as const,
  mias: ['formacion', 'competencias', 'mias'] as const,
  matriz: ['formacion', 'competencias', 'matriz'] as const,
  lista: ['formacion', 'competencias', 'lista'] as const,
  una: (id: number) => ['formacion', 'competencias', 'una', id] as const,
  cargos: ['formacion', 'cargos'] as const,
};

/* ── Lecturas ─────────────────────────────────────────────────────────── */

export function useMisCompetencias() {
  return useQuery({
    queryKey: clavesDeCompetencias.mias,
    queryFn: () => api.get<MisCompetencias>('/competencies/me/overview'),
  });
}

export function useMatriz(habilitado: boolean) {
  return useQuery({
    queryKey: clavesDeCompetencias.matriz,
    queryFn: () => api.get<{ positions: CargoDeLaMatriz[] }>('/competencies/matrix/by-position'),
    select: (r) => r.positions,
    enabled: habilitado,
  });
}

export function useCompetencias(habilitado: boolean) {
  return useQuery({
    queryKey: clavesDeCompetencias.lista,
    queryFn: () => api.get<CompetenciaResumen[]>('/competencies/'),
    enabled: habilitado,
  });
}

export function useCompetencia(id: number | null) {
  return useQuery({
    queryKey: clavesDeCompetencias.una(id ?? 0),
    queryFn: () => api.get<CompetenciaDetalle>(`/competencies/${id}`),
    enabled: id != null,
  });
}

/** El catálogo de cargos. `StaffRead` en el servidor: si no se puede leer, el
 *  campo se escribe a mano como antes, sin sugerencias. */
export function useCargos(habilitado: boolean) {
  return useQuery({
    queryKey: clavesDeCompetencias.cargos,
    queryFn: () => api.get<{ items?: Array<{ id: number; name: string }> }>('/admin/catalogs/positions'),
    select: (r) => r.items ?? [],
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

/* ── Escrituras ───────────────────────────────────────────────────────── */

/**
 * Tras cualquier cambio: la lista, la matriz, lo propio y el detalle abierto.
 *
 * En el vanilla el panel de una competencia se repintaba solo a sí mismo: al
 * cerrarlo, la lista de detrás seguía con los conteos de cursos y requisitos de
 * antes. Devuelve la promesa para que la mutación siga ocupada hasta que los
 * datos nuevos lleguen, y el siguiente clic parta de ellos.
 */
function useRefrescar() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: clavesDeCompetencias.todas });
}

export function useCrearCompetencia() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (cuerpo: CuerpoDeCompetencia) =>
      api.post<{ id: number; message?: string; restaurada?: boolean }>('/competencies/', cuerpo),
    onSuccess: () => refrescar(),
  });
}

/** El `PUT` sustituye todos los campos: `null` sí vacía aquí. */
export function useEditarCompetencia() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; cuerpo: CuerpoDeCompetencia }) =>
      api.put<{ message?: string }>(`/competencies/${v.id}`, v.cuerpo),
    onSuccess: () => refrescar(),
  });
}

export function useBorrarCompetencia() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message?: string }>(`/competencies/${id}`),
    onSuccess: () => refrescar(),
  });
}

export function useVincularCurso() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; course_id: number }) =>
      api.post<{ message?: string }>(`/competencies/${v.id}/courses`, { course_id: v.course_id }),
    onSuccess: () => refrescar(),
  });
}

export function useDesvincularCurso() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { id: number; course_id: number }) =>
      api.del<{ message?: string }>(`/competencies/${v.id}/courses/${v.course_id}`),
    onSuccess: () => refrescar(),
  });
}

export function useAgregarRequisito() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (v: { competency_id: number; target_position: string | null; area_id: number | null }) =>
      api.post<{ id: number }>('/competencies/requirements', v),
    onSuccess: () => refrescar(),
  });
}

export function useQuitarRequisito() {
  const refrescar = useRefrescar();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message?: string }>(`/competencies/requirements/${id}`),
    onSuccess: () => refrescar(),
  });
}
